import type { BillOfLadingData } from "./types/bill-of-lading-types";
import type { QuoteData } from "@shared/documents/quote-data";
import { api } from "@convex/_generated/api";
import type { FunctionArgs } from "convex/server";

type TemporaryDocumentRequest = FunctionArgs<
  typeof api.documents.tempArtifactState.request
>["request"];

type JobId = FunctionArgs<typeof api.documents.jobState.getMyStatus>["jobId"];

const DOCUMENT_POLL_TIMEOUT_MS = 10 * 60 * 1000;
const INITIAL_POLL_INTERVAL_MS = 250;
const STEADY_POLL_INTERVAL_MS = 1_000;

export type TemporaryQuoteData = Extract<
  TemporaryDocumentRequest,
  { type: "quote" }
>["data"];

export type GenerateTemporaryDocument = (args: {
  request: TemporaryDocumentRequest;
}) => Promise<{
  jobId: FunctionArgs<typeof api.documents.jobState.getMyStatus>["jobId"];
}>;

export type GetTemporaryDocumentStatus = (
  args: FunctionArgs<typeof api.documents.jobState.getMyStatus>,
) => Promise<{
  status: string;
  url: string | null;
  error?: string;
}>;

export type RequestCanonicalQuote = (args: {
  quoteId: FunctionArgs<
    typeof api.documents.canonicalRequests.requestQuote
  >["quoteId"];
}) => Promise<{
  jobId: FunctionArgs<typeof api.documents.jobState.getMyStatus>["jobId"];
}>;

export type RequestCanonicalStatement = (
  args: FunctionArgs<typeof api.documents.canonicalRequests.requestAccountStatement>,
) => Promise<{
  jobId: FunctionArgs<typeof api.documents.jobState.getMyStatus>["jobId"];
}>;

export class DocumentGenerationError extends Error {
  readonly reportable: boolean;

  constructor(message: string, reportable: boolean) {
    super(message);
    this.name = "DocumentGenerationError";
    this.reportable = reportable;
  }
}

export function prepareTemporaryQuoteData(data: QuoteData): TemporaryQuoteData {
  return {
    ...data,
    lineItems: data.lineItems.map(
      ({ specifications: _specifications, ...line }) => line,
    ),
  };
}

export function prepareTemporaryBillOfLadingData(
  data: BillOfLadingData,
): Extract<TemporaryDocumentRequest, { type: "bill_of_lading" }>["data"] {
  return {
    ...data,
    shipper: {
      ...data.shipper,
      address: {
        ...data.shipper.address,
        country: data.shipper.address.country ?? "US",
      },
    },
    consignee: {
      ...data.consignee,
      address: {
        ...data.consignee.address,
        country: data.consignee.address.country ?? "US",
      },
    },
    thirdPartyBilling: data.thirdPartyBilling
      ? {
          ...data.thirdPartyBilling,
          address: {
            ...data.thirdPartyBilling.address,
            country: data.thirdPartyBilling.address.country ?? "US",
          },
        }
      : undefined,
  };
}

function classifyGenerationError(error: unknown): DocumentGenerationError {
  const message = error instanceof Error ? error.message : String(error);
  if (/auth|required|sign in|session/i.test(message)) {
    return new DocumentGenerationError(
      "Your session expired. Sign in again, then retry the download.",
      false,
    );
  }
  if (/invalid|too large|exceed/i.test(message)) {
    return new DocumentGenerationError(
      "The document data is invalid. Refresh the page and try again.",
      false,
    );
  }
  return new DocumentGenerationError(
    "The PDF service could not create this document. The failure was reported; please try again.",
    true,
  );
}

export async function generateDocumentBlob(
  request: TemporaryDocumentRequest,
  requestTemporaryDocument: GenerateTemporaryDocument,
  getTemporaryDocumentStatus: GetTemporaryDocumentStatus,
  fetchDocument: typeof fetch = fetch,
): Promise<Blob> {
  return await downloadRequestedArtifact(
    async () => (await requestTemporaryDocument({ request })).jobId,
    getTemporaryDocumentStatus,
    fetchDocument,
  );
}

export async function generateCanonicalQuoteBlob(
  quoteId: FunctionArgs<
    typeof api.documents.canonicalRequests.requestQuote
  >["quoteId"],
  requestQuote: RequestCanonicalQuote,
  getStatus: GetTemporaryDocumentStatus,
  fetchDocument: typeof fetch = fetch,
): Promise<Blob> {
  return await downloadRequestedArtifact(
    async () => (await requestQuote({ quoteId })).jobId,
    getStatus,
    fetchDocument,
  );
}

export async function generateCanonicalStatementBlob(
  request: FunctionArgs<typeof api.documents.canonicalRequests.requestAccountStatement>,
  requestStatement: RequestCanonicalStatement,
  getStatus: GetTemporaryDocumentStatus,
  fetchDocument: typeof fetch = fetch,
): Promise<Blob> {
  return await downloadRequestedArtifact(
    async () => (await requestStatement(request)).jobId,
    getStatus,
    fetchDocument,
  );
}

export async function downloadRequestedArtifact(
  requestJob: () => Promise<JobId>,
  getStatus: GetTemporaryDocumentStatus,
  fetchDocument: typeof fetch = fetch,
): Promise<Blob> {
  try {
    const url = await waitForArtifact(await requestJob(), getStatus);
    const response = await fetchDocument(url, { cache: "no-store" });
    if (!response.ok) {
      throw new Error(`Generated PDF download failed (${response.status})`);
    }
    return await response.blob();
  } catch (error) {
    if (error instanceof DocumentGenerationError) throw error;
    throw classifyGenerationError(error);
  }
}

export async function waitForArtifact(
  jobId: FunctionArgs<typeof api.documents.jobState.getMyStatus>["jobId"],
  getStatus: GetTemporaryDocumentStatus,
): Promise<string> {
  const deadline = Date.now() + DOCUMENT_POLL_TIMEOUT_MS;
  let attempt = 0;
  while (Date.now() < deadline) {
    const result = await getStatus({ jobId });
    if (result.status === "succeeded" && result.url) return result.url;
    if (result.status === "failed") {
      throw new Error(result.error ?? "PDF generation failed");
    }
    const interval =
      attempt < 40 ? INITIAL_POLL_INTERVAL_MS : STEADY_POLL_INTERVAL_MS;
    await new Promise((resolve) => setTimeout(resolve, interval));
    attempt += 1;
  }
  throw new Error("PDF generation timed out");
}

export function downloadDocumentBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
